// ---------------------------------------------------------------------------
// C1: Content Morphing Hook
// ---------------------------------------------------------------------------
import { useCallback, useState } from "react";
import { apiFetch, ApiError } from "@/lib/api-client";

const SCHEDULABLE_PLATFORMS = new Set(["facebook", "instagram", "linkedin"]);

export interface MorphPlatformVersion {
  platform: string;
  format: string;
  title: string;
  content: string;
  hashtags: string[];
  characterCount: number;
  estimatedEngagement: "low" | "medium" | "high";
  suggestedMediaType: string;
  optimalPostTime?: string;
  notes: string;
}

interface MorphResult {
  originalIdea: string;
  versions: MorphPlatformVersion[];
  generatedAt: string;
}

interface MorphInput {
  idea: string;
  brandContext: {
    businessName: string;
    industry: string;
    tone: string;
    tagline?: string;
    targetAudience?: string;
  };
  platforms?: string[];
}

interface ApiDataResponse<T> {
  data: T;
}

function toErrorMessage(err: unknown): string {
  if (err instanceof ApiError) return `${err.status}: ${err.message}`;
  return err instanceof Error ? err.message : "Unknown error";
}

function normalizeContentType(mediaType: string): "text" | "image" | "video" | "carousel" {
  if (mediaType === "video") return "video";
  if (mediaType === "carousel") return "carousel";
  if (mediaType === "image" || mediaType === "infographic") return "image";
  return "text";
}

export function useContentMorphing() {
  const [data, setData] = useState<MorphResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const mutate = useCallback(async (input: MorphInput): Promise<MorphResult> => {
    setIsLoading(true);
    setError(null);

    const platforms = input.platforms?.length ? input.platforms : ["linkedin", "instagram", "facebook"];
    const result: MorphResult = {
      originalIdea: input.idea,
      versions: platforms.map((platform) => ({
        platform,
        format: "draft",
        title: input.idea,
        content: input.idea,
        hashtags: [],
        characterCount: input.idea.length,
        estimatedEngagement: "medium",
        suggestedMediaType: "text",
        notes: "Local draft only. Social morphing is disabled until a mounted backend endpoint exists.",
      })),
      generatedAt: new Date().toISOString(),
    };

    setData(result);
    setIsLoading(false);
    return result;
  }, []);

  return { mutate, isLoading, error, data };
}

export function useScheduleMorphedPost() {
  type ScheduleInput = {
    platform: string;
    content: string;
    hashtags: string[];
    mediaType: string;
    scheduledAt?: string;
  };
  type ScheduleResult = { success: boolean; postId: string };

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ScheduleResult | null>(null);

  const mutate = useCallback(async (input: ScheduleInput): Promise<ScheduleResult | null> => {
    if (!SCHEDULABLE_PLATFORMS.has(input.platform)) {
      setData(null);
      setError(`${input.platform} scheduling is not mounted yet. Copy the version or use Facebook, Instagram, or LinkedIn.`);
      return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      const content = input.hashtags.length > 0
        ? `${input.content}\n\n${input.hashtags.join(" ")}`
        : input.content;
      const response = await apiFetch<ApiDataResponse<{ postId: string }>>("/crm/social/calendar", {
        method: "POST",
        body: JSON.stringify({
          content,
          platforms: [input.platform],
          contentType: normalizeContentType(input.mediaType),
          scheduledAt: input.scheduledAt,
        }),
      });
      const result = { success: true, postId: response.data.postId };
      setData(result);
      return result;
    } catch (err) {
      setData(null);
      setError(toErrorMessage(err));
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { mutate, isLoading, error, data };
}
